import React, { useState, useEffect } from "react";
import { Grid, TextField, Typography } from "@mui/material";
import * as Yup from "yup";
import { updateOrganisationAclDetails } from "@/store/slices/userSlice";
import regex from "@/utils/regex";

/** Initial state from organisation.acl (fallback to organisation root for backward compat) */
const getAclDetailsSource = (organisation) => {
  const acl = organisation?.acl ?? organisation;
  return acl || {};
};

const ACL_FIELD_GETTERS = {
  number: (o) => getAclDetailsSource(o)?.number ?? o?.number ?? "",
  name: (o) => getAclDetailsSource(o)?.name ?? o?.name ?? "",
  abn: (o) => getAclDetailsSource(o)?.abn ?? o?.abn ?? "",
  complaintsEmail: (o) => getAclDetailsSource(o)?.complaintsEmail ?? o?.complaintsEmail ?? "",
  complaintsPhoneNumber: (o) => getAclDetailsSource(o)?.complaintsPhoneNumber ?? o?.complaintsPhoneNumber ?? "",
  complaintsTelephoneNumber: (o) =>
    getAclDetailsSource(o)?.complaintsTelephoneNumber ?? o?.complaintsTelephoneNumber ?? "",
  complaintsAddress: (o) => getAclDetailsSource(o)?.complaintsAddress ?? o?.complaintsAddress ?? "",
  privacyPolicyLink: (o) => getAclDetailsSource(o)?.privacyPolicyLink ?? o?.privacyPolicyLink ?? "",
};

const getCurrentAclValue = (organisation, fieldName) => {
  const getter = ACL_FIELD_GETTERS[fieldName];
  return getter ? getter(organisation) : (getAclDetailsSource(organisation)?.[fieldName] ?? "");
};

const initialAclFieldErrors = {
  number: "",
  name: "",
  abn: "",
  complaintsEmail: "",
  complaintsPhoneNumber: "",
  complaintsTelephoneNumber: "",
  complaintsAddress: "",
  privacyPolicyLink: "",
};

const aclValidationSchema = Yup.object({
  name: Yup.string().optional().matches(regex.name, { message: "Only string allowed", excludeEmptyString: true }),
  number: Yup.string()
    .optional()
    .matches(regex.allowOnlyNumber, {
      message: "Please enter australian credit licence number",
      excludeEmptyString: true,
    }),
  abn: Yup.string().optional().matches(regex.ABN, { message: "Please enter valid ABN", excludeEmptyString: true }),
  complaintsEmail: Yup.string()
    .optional()
    .matches(regex.email, { message: "Please enter organisation acl complaints email", excludeEmptyString: true }),
  complaintsPhoneNumber: Yup.string()
    .optional()
    .matches(regex.phoneNumber, {
      message: "Please enter organisation acl complaints phone number",
      excludeEmptyString: true,
    }),
  complaintsTelephoneNumber: Yup.string().optional().matches(regex.telephoneNumber, {
    message: "Please enter organisation acl complaints telephone number",
    excludeEmptyString: true,
  }),
  complaintsAddress: Yup.string().optional().matches(regex.addressRegex, {
    message: "Please enter organisation acl complaints address",
    excludeEmptyString: true,
  }),
  privacyPolicyLink: Yup.string().optional().matches(regex.url, {
    message: "Please enter organisation acl privacy policy link",
    excludeEmptyString: true,
  }),
});

const getInitialAclLenders = (organisation) => {
  const source = getAclDetailsSource(organisation);
  const fromState = source?.lenders ?? organisation?.lenders ?? [];
  if (!Array.isArray(fromState)) return { lenders: ["", "", "", "", "", ""], lendersInput: "" };
  const padded = [...fromState.slice(0, 6)];
  while (padded.length < 6) padded.push("");
  const lendersInput = padded
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(", ");
  return { lenders: padded, lendersInput };
};

const getInitialAclState = (organisation) => {
  if (!organisation) return null;
  const source = getAclDetailsSource(organisation);
  return {
    name: source?.name ?? "",
    number: source?.number ?? "",
    abn: source?.abn ?? "",
    complaintsEmail: source?.complaintsEmail ?? "",
    complaintsPhoneNumber: source?.complaintsPhoneNumber ?? "",
    complaintsTelephoneNumber: source?.complaintsTelephoneNumber ?? "",
    complaintsAddress: source?.complaintsAddress ?? "",
    privacyPolicyLink: source?.privacyPolicyLink ?? "",
    ...getInitialAclLenders(organisation),
  };
};

const OrganisationAclDetailsSection = ({ organisation, dispatch, isOrgEditable, orgAccessComponent }) => {
  const organisationId = organisation?._id;
  const [state, setState] = useState(null);
  const [fieldErrors, setFieldErrors] = useState(initialAclFieldErrors);

  useEffect(() => {
    setState(getInitialAclState(organisation));
  }, [organisation]);

  const validateField = async (fieldName, value) => {
    try {
      await aclValidationSchema.validateAt(fieldName, { [fieldName]: value });
      setFieldErrors((prev) => ({ ...prev, [fieldName]: "" }));
    } catch (error) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: error.message }));
    }
  };

  const handle = {
    onChangeField: (value, name) => {
      setState((prev) => (prev ? { ...prev, [name]: value } : prev));
      validateField(name, value);
    },
    onBlurField: (value, name) => {
      try {
        const oldVal = getCurrentAclValue(organisation, name);
        if (oldVal === value) return;
        if (fieldErrors[name] !== "") return;

        setState((prev) => (prev ? { ...prev, [name]: value } : prev));

        if (!organisationId) return;
        dispatch(updateOrganisationAclDetails({ organisationId, [name]: value }));
      } catch (error) {
        const newErrors = {};
        error?.inner?.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setFieldErrors((prev) => ({ ...prev, ...newErrors }));
      }
    },
    onBlurTopLenders: () => {
      const raw = (state?.lendersInput ?? "").trim();
      const lenders = raw
        ? raw
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 6)
        : [];
      const padded = [...lenders, ...Array(6 - lenders.length).fill("")];
      const normalizedInput = lenders.join(", ");
      setState((prev) => (prev ? { ...prev, lenders: padded, lendersInput: normalizedInput } : prev));
      const source = getAclDetailsSource(organisation);
      const existing = source?.lenders ?? organisation?.lenders ?? [];
      const same =
        Array.isArray(existing) &&
        lenders.length === existing.length &&
        lenders.every((v, i) => v === (existing[i] ?? ""));
      if (!same && organisationId) {
        dispatch(updateOrganisationAclDetails({ organisationId, lenders }));
      }
    },
  };

  if (!state) return null;

  return (
    <>
      <Grid item xs={8}>
        <Typography variant="subtitle1" style={{ margin: "16px 0 8px 0", fontWeight: "600" }}>
          ACL (Australian Credit Licence)
        </Typography>
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="ACL Credit Licence Number"
          variant="filled"
          type="text"
          value={state.number}
          error={!!fieldErrors.number}
          helperText={fieldErrors.number}
          onChange={(e) => handle.onChangeField(e.target.value, "number")}
          onBlur={(e) => handle.onBlurField(e.target.value, "number")}
          disabled={!isOrgEditable}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="ACL Name"
          variant="filled"
          type="text"
          value={state.name}
          error={!!fieldErrors.name}
          helperText={fieldErrors.name}
          onChange={(e) => handle.onChangeField(e.target.value, "name")}
          onBlur={(e) => handle.onBlurField(e.target.value, "name")}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
          disabled={!isOrgEditable}
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="ACL ABN"
          variant="filled"
          type="text"
          value={state.abn}
          error={!!fieldErrors.abn}
          helperText={fieldErrors.abn}
          onChange={(e) => handle.onChangeField(e.target.value, "abn")}
          onBlur={(e) => handle.onBlurField(e.target.value, "abn")}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
          disabled={!isOrgEditable}
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="ACL Complaints Email"
          variant="filled"
          type="text"
          value={state.complaintsEmail}
          error={!!fieldErrors.complaintsEmail}
          helperText={fieldErrors.complaintsEmail}
          onChange={(e) => handle.onChangeField(e.target.value, "complaintsEmail")}
          onBlur={(e) => handle.onBlurField(e.target.value, "complaintsEmail")}
          disabled={!isOrgEditable}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      {/* Phone Number should not visible to client */}
      {!orgAccessComponent.isShowTopLenders && (
        <Grid item xs={8}>
          <TextField
            label="ACL Complaints Phone Number"
            variant="filled"
            type="text"
            value={state.complaintsPhoneNumber}
            error={!!fieldErrors.complaintsPhoneNumber}
            helperText={fieldErrors.complaintsPhoneNumber}
            onChange={(e) => handle.onChangeField(e.target.value, "complaintsPhoneNumber")}
            onBlur={(e) => handle.onBlurField(e.target.value, "complaintsPhoneNumber")}
            disabled={!isOrgEditable}
            size="small"
            style={{ margin: "0 0 5px 0" }}
            fullWidth
          />
        </Grid>
      )}

      <Grid item xs={8}>
        <TextField
          label="ACL Complaints Telephone Number"
          variant="filled"
          type="text"
          value={state.complaintsTelephoneNumber}
          error={!!fieldErrors.complaintsTelephoneNumber}
          helperText={fieldErrors.complaintsTelephoneNumber}
          onChange={(e) => handle.onChangeField(e.target.value, "complaintsTelephoneNumber")}
          onBlur={(e) => handle.onBlurField(e.target.value, "complaintsTelephoneNumber")}
          disabled={!isOrgEditable}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="ACL Complaints Business Address"
          variant="filled"
          type="text"
          value={state.complaintsAddress}
          error={!!fieldErrors.complaintsAddress}
          helperText={fieldErrors.complaintsAddress}
          onChange={(e) => handle.onChangeField(e.target.value, "complaintsAddress")}
          onBlur={(e) => handle.onBlurField(e.target.value, "complaintsAddress")}
          disabled={!isOrgEditable}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      <Grid item xs={8}>
        <TextField
          label="ACL Privacy Policy Link"
          variant="filled"
          type="text"
          value={state.privacyPolicyLink}
          error={!!fieldErrors.privacyPolicyLink}
          helperText={fieldErrors.privacyPolicyLink}
          onChange={(e) => handle.onChangeField(e.target.value, "privacyPolicyLink")}
          onBlur={(e) => handle.onBlurField(e.target.value, "privacyPolicyLink")}
          disabled={!isOrgEditable}
          size="small"
          style={{ margin: "0 0 5px 0" }}
          fullWidth
        />
      </Grid>

      {orgAccessComponent.isShowTopLenders && (
        <Grid item xs={8}>
          <Typography variant="body2" style={{ margin: "0 0 5px 0", fontWeight: "600" }}>
            ACL Top 6 Lenders by Volume
          </Typography>
          <TextField
            label="Lenders"
            placeholder="e.g. ANZ, Azora, etc..."
            variant="filled"
            type="text"
            value={state.lendersInput ?? ""}
            onChange={(e) => setState((prev) => (prev ? { ...prev, lendersInput: e.target.value } : prev))}
            onBlur={handle.onBlurTopLenders}
            disabled={!isOrgEditable}
            size="small"
            style={{ margin: "0 0 5px 0" }}
            fullWidth
            helperText="Enter up to 6 lenders separated by commas"
          />
        </Grid>
      )}
    </>
  );
};

export default OrganisationAclDetailsSection;
